import { type FC, type ReactNode } from 'react';

import { BLOCKS, type Document } from '@contentful/rich-text-types';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { RichText } from '@vakantiesnl/components/src/molecules/RichText';
import { type RowContent } from '@vakantiesnl/types';

import { useCustomStyles } from './TwoColumnRow.style';
import { useBreakpoints } from '../../utils/useBreakpoints';

export type TwoColumnRowProps = {
	/** Content rendered in the 2 cells of the row */
	rowContent: RowContent;
	/** Ratio of the right column compared to the left */
	contentColumnRatio?: number;
	/** Align the content in the right column */
	centerOrFlexStart?: 'center' | 'flex-start';
};

export const TwoColumnRow: FC<TwoColumnRowProps> = ({
	rowContent: { rowTitle, cellContent },
	contentColumnRatio = 6,
	centerOrFlexStart = 'center',
}) => {
	const { classes } = useCustomStyles({ contentColumnRatio, centerOrFlexStart });
	const { isTabletOrIsDesktop } = useBreakpoints();

	const defaultFontVariant = isTabletOrIsDesktop ? 'bodyMd' : 'bodySm';

	return (
		<tr className={classes.twoColumnRow}>
			<td className={classes.titleContainer}>
				<Typography variant={cellContent ? defaultFontVariant : 'headingSm'} as="h5" className={classes.title}>
					{rowTitle}
				</Typography>
			</td>
			{cellContent && (
				<td className={classes.cellContentContainer}>
					{isDocument(cellContent) ? (
						<RichText document={cellContent} />
					) : (
						<Typography variant={defaultFontVariant} as="div">
							{cellContent}
						</Typography>
					)}
				</td>
			)}
		</tr>
	);
};

function isDocument(value: ReactNode | Document): value is Document {
	if (value && typeof value === 'object' && 'nodeType' in value && value.nodeType === BLOCKS.DOCUMENT) {
		return true;
	}

	return false;
}
